import {
  Box,
  FlexBox,
  IconButton,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { OpeningHours } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';


import type { ModifiedListItem } from '../../views/schedule/schedule.context';
import { DateCard } from '../dateCard/dateCard';

import styles from './reviewConflictsModal.module.scss';

type Props = {
  conflicts: ModifiedListItem;
  resolvedDates?: string[];
  onClose: VoidFunction;
  onResolve: (modifiedListItems: ModifiedListItem) => void;
};

function getConflictingExceptions(
  conflicts: ModifiedListItem,
  conflictedDate: string,
): OpeningHours[] {
  return Object.values(conflicts).filter(
    ({ next_date }) => next_date === conflictedDate,
  );
}

export const ReviewConflictsModal = ({
  conflicts,
  resolvedDates: propResolvedDates = [],
  onClose,
  onResolve,
}: Props): JSX.Element => {
  const [activeConflictGroup, setActiveConflictGroup] = useState<number>(1);
  const [resolvedDates, setResolvedDates] =
    useState<string[]>(propResolvedDates);
  const [resolvedConflicts, setResolvedConflicts] =
    useState<ModifiedListItem>(conflicts);

  const conflictedDates = useMemo(
    () => [
      ...new Set(
        Object.values(conflicts).map(({ next_date }) => next_date as string),
      ),
    ],
    [conflicts],
  );
  const { length: conflictsLength } = conflictedDates;

  const handleSelect = useCallback(
    (exception: OpeningHours, conflictedDate: string) => () => {
      setResolvedDates((prevResolvedDates) => [
        ...new Set([...prevResolvedDates, conflictedDate]),
      ]);

      const currentConflicts = getConflictingExceptions(
        resolvedConflicts,
        conflictedDate,
      ).reduce((acc, curr) => {
        const { overridden_by, ...rest } = curr;

        return {
          ...acc,
          [curr.id]: {
            ...rest,
            ...(curr.id !== exception.id && {
              overridden_by: {
                id: exception.id,
                date: exception.next_date,
              },
            }),
          },
        };
      }, {});

      setResolvedConflicts((prevResolvedDates) => ({
        ...prevResolvedDates,
        ...currentConflicts,
      }));

      if (activeConflictGroup < conflictsLength) {
        setTimeout(() => {
          setActiveConflictGroup(
            (prevActiveConflictGroup) => prevActiveConflictGroup + 1,
          );
        }, 500);
      }
    },
    [activeConflictGroup, conflictsLength, resolvedConflicts],
  );

  const handleSubmit = useCallback(() => {
    ampli.businessHoursScheduleReviewFormSubmitted();
    onResolve(resolvedConflicts);
    onClose();
  }, [resolvedConflicts, onClose, onResolve]);

  const handleClose = useCallback(() => {
    ampli.businessHoursScheduleReviewFormCancelled();
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen onClose={handleClose}>
      <Box width="600px">
        <form onSubmit={handleSubmit}></form>
        <ModalHead title="Review conflicts" onClose={handleClose}>
          <Text.Paragraph className={styles.description}>
            Please choose the business hours that apply for the below dates:
          </Text.Paragraph>
        </ModalHead>
        <ModalBody>
          {conflictsLength > 1 && (
            <FlexBox alignItems="center" justifyContent="space-between">
              <IconButton
                disabled={activeConflictGroup === 1}
                icon="angle-left"
                iconSize={24}
                onClick={() =>
                  setActiveConflictGroup(
                    (prevActiveConflictGroup) => prevActiveConflictGroup - 1,
                  )
                }
                variant="tertiary"
              />
              <Text.Paragraph className={styles.conflictControlText}>
                Conflict {activeConflictGroup} of {conflictsLength}
              </Text.Paragraph>
              <IconButton
                disabled={activeConflictGroup === conflictsLength}
                icon="angle-right"
                onClick={() =>
                  setActiveConflictGroup(
                    (prevActiveConflictGroup) => prevActiveConflictGroup + 1,
                  )
                }
                variant="tertiary"
              />
            </FlexBox>
          )}
          {conflictedDates.map((conflictedDate, index) => {
            const isResolved = resolvedDates.indexOf(conflictedDate) > -1;

            return (
              <div
                key={conflictedDate}
                className={classNames(styles.conflictList, {
                  [styles.conflictListHidden]:
                    activeConflictGroup !== index + 1,
                })}
              >
                {getConflictingExceptions(
                  resolvedConflicts,
                  conflictedDate,
                ).map((exception) => {
                  return (
                    <DateCard
                      key={exception.id}
                      exception={exception}
                      isSelected={isResolved && !exception.overridden_by}
                      onClick={handleSelect(exception, conflictedDate)}
                    />
                  );
                })}
              </div>
            );
          })}
        </ModalBody>
        <ModalFoot
          cancelOnClick={handleClose}
          confirmProps={{
            disabled: resolvedDates.length !== conflictsLength,
          }}
          confirmName="Resolve"
          confirmOnClick={handleSubmit}
        />
      </Box>
    </Modal>
  );
};
